import React from "react";
import "./desktop-header.css";
import { useAppSelector } from "../../app/hooks";
import { resetUser, selectHasUserPropertySetting, selectUser, selectUserRole, setUser } from "../../slices/user";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { resetToken } from "../../slices/token";
import UserModal from "../Modals/UserModal";
import { Context } from "../../context/ContextProvider/ContextProvider";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { selectLabels } from "../../slices/language";
import api, { CreateUserPayload } from "../../api";
import { showError } from "../../utils/helpers";
import IconifyIcon from "../IconifyIcon";

interface DesktopHeaderProps {
  title: string;
  isGuard?: boolean;
}

const DesktopHeader = ({ title, isGuard }: DesktopHeaderProps) => {
  const labels = useAppSelector(selectLabels);
  const user = useAppSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userRole = useAppSelector(selectUserRole);
  const hasSecUserPerms = useAppSelector(selectHasUserPropertySetting("sec_user_perms"));
  const isSecondaryHostRestricted = userRole === "secondary_host" && !hasSecUserPerms;

  const {
    showDesktopMenu,
    setSelectedAccessPointGuard,
    selectedAccessPointGuard,
    setShowDesktopMenu,
  } = React.useContext(Context);

  const [showUserDetails, setShowUserDetails] = React.useState<boolean>(false);
  const [showMissingAccessPoint, setShowMissingAccessPoint] =
    React.useState<boolean>(false);

  const userInitial = user?.name ? user?.name.toUpperCase().charAt(0) : "";

  const accessPoints = React.useMemo(() => {
    if (!isGuard) {
      return [];
    }
    if (user && user?.access_points) {
      const fixedAccessPoints = user?.access_points.map((item) => {
        return {
          label: item?.name,
          value: item?.id,
        };
      });
      return fixedAccessPoints;
    }
    return [];
  }, [isGuard, user]);

  const onLogout = () => {
    dispatch(resetToken());
    dispatch(resetUser());
    navigate("/");
    setSelectedAccessPointGuard("");
    setShowDesktopMenu(true);
  };

  React.useEffect(() => {
    if (isGuard) {
      if (
        user?.last_access_selected_id &&
        user?.last_access_selected_id !== 0
      ) {
        setSelectedAccessPointGuard(`${user?.last_access_selected_id}`);
        setShowMissingAccessPoint(false);
      } else {
        setShowMissingAccessPoint(true);
      }
      setShowDesktopMenu(false);
    }
  }, [
    isGuard,
    setSelectedAccessPointGuard,
    setShowDesktopMenu,
    user?.last_access_selected_id,
  ]);

  const updateLastAccessPoint = async (selectedValue: number) => {
    if (selectedValue && user) {
      const payload: CreateUserPayload = {
        last_access_selected_id: selectedValue,
      };
      try {
        const response = await api.updateUser(user?.id, payload);
        if (response) {
          const currentUser = {
            ...user,
            last_access_selected_id: selectedValue,
          };
          dispatch(setUser(currentUser));
        }
      } catch (error) {
        showError(dispatch, labels.generic_error);
      }
    }
  };

  return (
    <div
      className={`desktop-header-container px-8 ${
        showDesktopMenu ? "" : "collapsed"
      } ${isSecondaryHostRestricted ? "secondary-host-restricted" : ""}`}
    >
      <UserModal
        showUserDetails={showUserDetails}
        setShowUserDetails={setShowUserDetails}
        onLogout={onLogout}
      />
      <div
        className="w-100 border-box flex align-items-center justify-content-between"
        style={{ height: "60px" }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <p className="main-title">{title}</p>
          {isGuard && (
            <div
              style={{ marginLeft: 40, display: "flex", alignItems: "center" }}
            >
              <div style={{ width: 300 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="access_points">
                    {labels.access_point}
                  </InputLabel>
                  <Select
                    labelId={"access_points"}
                    id={"access_points"}
                    label={labels.access_point}
                    value={selectedAccessPointGuard}
                    onChange={(event: SelectChangeEvent) => {
                      const newSelectedAccessPoint = event.target
                        .value as string;
                      setSelectedAccessPointGuard(newSelectedAccessPoint);
                      updateLastAccessPoint(Number(newSelectedAccessPoint));
                      if (showMissingAccessPoint) {
                        setShowMissingAccessPoint(false);
                      }
                    }}
                  >
                    {accessPoints.map((item, index) => (
                      <MenuItem
                        key={`${index}-${item?.value}`}
                        value={item?.value}
                      >
                        {item?.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              {showMissingAccessPoint && (
                <div className="flex align-items-center gap-3 red-color">
                  <IconifyIcon icon={"icon-park-solid:left-two"} />
                  <p className="body-text-sm red-color font-w-500">
                    Selecciona un punto de acceso
                  </p>
                </div>
              )}
              {/* <p style={{ marginLeft: 20 }}>{labels.visitors_inside}: 0</p> */}
            </div>
          )}
        </div>
        <div className="user-container">
          <p className="body-text-md pr-6 pl-2">{user?.email}</p>
          <div
            className="user-initial-container"
            onClick={() => setShowUserDetails(true)}
          >
            <p style={{ color: "white", fontWeight: 400, fontSize: 18 }}>
              {userInitial}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesktopHeader;
